/*
 * The sentence of a system row (spec 05 §2.6, protocol.md §2.5.3).
 *
 * A system message is stored as ids and enums in `meta` with an empty `markdown`, because rendered
 * text is never stored as truth: the server mints `kind` plus the payload, and the sentence is
 * composed here, in the reader's language, out of the display names the model already holds.
 *
 * Three kinds read more than one way, which is why there are twenty-one texts for sixteen kinds: a
 * topic that was removed is not a topic that changed, a direct conversation that became a *named* one
 * is not one that became a group, and the switch for AI chats reads by its direction and its place
 * (below). A kind this build does not know renders as the empty string, which the
 * row then omits — a client one version behind a server never prints a raw enum.
 *
 * Three kinds name groups — a channel opened to groups, a group taken off it — and an administrator
 * who joined in the open names a person. A group is named the way the reader's model names it
 * (`GroupNameLookup`, built by the caller from `model.groups`), and a group the model does not know —
 * deleted since, or never visible to the reader — reads `chat.system.goneGroup`, never a number.
 *
 * One kind reads by the conversation it stands in: `agents_changed`, the switch for AI chats, says
 * "this channel" in a channel or a private channel and "this conversation" in a direct or group one
 * (S-19 §6). The kind and its payload `{by, available}` are the same everywhere; the words are the
 * place's, which is why the caller hands in whether the place is a direct conversation.
 *
 * One kind is not chat's own: `announcement` rows were written for a module teamBase no longer has,
 * and they stay in the history like every message. Their payload named that module's things, so
 * they are drawn as one neutral line (`chat.system.retiredNotice`) that says only what they were.
 */

import { formatList } from "../../../core/i18n/format.ts"
import { t } from "../i18n/t.ts"
import type { MessageView } from "../wire/views.ts"

/** What the renderer needs of the model: a display name per account id. */
export type NameLookup = (userId: number) => string

/** And a name per group id; an id the model does not know answers `chat.system.goneGroup`. */
export type GroupNameLookup = (groupId: number) => string

/** The union of the meta payloads systemmsg.go writes; every field is absent on most kinds. */
interface SystemMeta {
	by?: number
	user?: number
	users?: number[]
	from?: string
	to?: string
	topic?: string
	added?: number[]
	groups?: number[]
	group?: number
	visibleFromSeq?: number
	available?: boolean
}

/** The stored word of a retired module's notice: the one place it is named on the client. */
export const ANNOUNCEMENT_KIND = "announcement"

function metaOf(msg: MessageView): SystemMeta {
	return msg.meta ?? {}
}

/** The list form of several people: "Anna, Bea und Chris" is the renderer's job, not the server's. */
function joinNames(ids: readonly number[], name: NameLookup): string {
	return formatList(ids.map(name).filter((n) => n !== ""))
}

/**
 * The sentence of one system row. `direct` says whether the conversation it stands in is a direct or
 * group conversation, for the one kind that is worded by its place.
 */
export function systemText(
	msg: MessageView,
	name: NameLookup,
	groupName: GroupNameLookup,
	direct: boolean,
): string {
	const meta = metaOf(msg)
	const by = name(meta.by ?? 0)
	const who = name(meta.user ?? 0)
	switch (msg.kind) {
		case "conversation_created":
			return t("chat.system.conversationCreated", { by })
		case "conversation_renamed":
			return t("chat.system.conversationRenamed", { by, from: meta.from ?? "", to: meta.to ?? "" })
		case "topic_changed":
			return meta.topic === undefined || meta.topic === ""
				? t("chat.system.topicCleared", { by })
				: t("chat.system.topicChanged", { by, topic: meta.topic })
		case "purpose_changed":
			return t("chat.system.purposeChanged", { by })
		case "conversation_archived":
			return t("chat.system.conversationArchived", { by })
		case "conversation_unarchived":
			return t("chat.system.conversationUnarchived", { by })
		case "member_added":
			return t("chat.system.memberAdded", { by, users: joinNames(meta.users ?? [], name) })
		case "member_joined":
			return t("chat.system.memberJoined", { user: who })
		case "member_left":
			return t("chat.system.memberLeft", { user: who })
		case "member_removed":
			return t("chat.system.memberRemoved", { by, user: who })
		case "conversation_promoted":
			return meta.to === "channel" || meta.to === "private"
				? t("chat.system.conversationPromotedToNamed", { by })
				: t("chat.system.conversationPromoted", { by })
		case "owner_added":
			return t("chat.system.ownerAdded", {
				by,
				groups: formatList((meta.groups ?? []).map(groupName)),
			})
		case "owner_removed":
			return t("chat.system.ownerRemoved", { by, group: groupName(meta.group ?? 0) })
		case "admin_joined":
			return t("chat.system.adminJoined", { user: who })
		case "agents_changed":
			if (direct) {
				return meta.available === true
					? t("chat.system.agentsOnDirect", { by })
					: t("chat.system.agentsOffDirect", { by })
			}
			return meta.available === true
				? t("chat.system.agentsOn", { by })
				: t("chat.system.agentsOff", { by })
		case ANNOUNCEMENT_KIND:
			return t("chat.system.retiredNotice")
		// A kind this build does not know is a row an older client leaves out rather than printing
		// an enum.
		default:
			return ""
	}
}
